import React from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Badge } from '../../ui/components/Badge';
import {
  FeatherSlidersHorizontal,
  FeatherChevronDown,
  FeatherSearch,
  FeatherX,
  FeatherChevronRight,
} from '@subframe/core';
import { getRarityInfo, getRarityInfoInCategory } from '../../utils/formatters';
import type { NFTFiltersState } from '../../hooks/useNFTFilters';
import { getAvailableSortOptions, ownershipOptions } from '../../hooks/useNFTFilters';

export interface TraitStat {
  traitType: string;
  values: { value: string; count: number; percentage: number }[];
}

interface NFTFilterBarProps {
  filters: NFTFiltersState;
  traitStats: TraitStat[];
  hasVariablePoints?: boolean;
  isAuthenticated?: boolean;
  hideOwnershipFilter?: boolean;
  rightContent?: React.ReactNode;
  activeFiltersRightContent?: React.ReactNode;
}

export const NFTFilterBar: React.FC<NFTFilterBarProps> = ({
  filters,
  traitStats,
  hasVariablePoints = false,
  isAuthenticated = false,
  hideOwnershipFilter = false,
  rightContent,
  activeFiltersRightContent,
}) => {
  const {
    sortOption,
    minPrice,
    maxPrice,
    minRarity,
    maxRarity,
    minPoints,
    maxPoints,
    searchQuery,
    selectedTraits,
    expandedTraits,
    ownershipFilter,
    showSortDropdown,
    showFilters,
    showOwnershipDropdown,
    setMinPrice,
    setMaxPrice,
    setMinRarity,
    setMaxRarity,
    setMinPoints,
    setMaxPoints,
    setSearchQuery,
    setOwnershipFilter,
    setShowSortDropdown,
    setShowFilters,
    setShowOwnershipDropdown,
    handleSortChange,
    handleFilterApply,
    handleFilterClear,
    handleClearPriceFilter,
    handleClearRarityFilter,
    handleClearPointsFilter,
    handleTraitToggle,
    toggleTraitExpanded,
    getCurrentSortLabel,
    setCurrentPage,
    hasActiveFilters,
  } = filters;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {/* Filters and Search */}
      <div className="flex w-full items-center gap-2 flex-wrap relative">
        {/* Ownership Filter Dropdown */}
        {!hideOwnershipFilter && (
          <div className="relative">
            <Button
              variant="neutral-secondary"
              iconRight={<FeatherChevronDown />}
              onClick={() => {
                if (isAuthenticated) {
                  setShowOwnershipDropdown(!showOwnershipDropdown);
                  setShowSortDropdown(false);
                  setShowFilters(false);
                }
              }}
              disabled={!isAuthenticated}
              className={!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {ownershipOptions.find(opt => opt.value === ownershipFilter)?.label || 'All NFTs'}
            </Button>

            {showOwnershipDropdown && isAuthenticated && (
              <div className="absolute top-full left-0 mt-2 w-40 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-10">
                {ownershipOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setOwnershipFilter(option.value);
                      setCurrentPage(1);
                      setShowOwnershipDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                      ownershipFilter === option.value ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filters Button */}
        <Button
          variant="neutral-secondary"
          icon={<FeatherSlidersHorizontal />}
          onClick={() => {
            setShowFilters(!showFilters);
            setShowOwnershipDropdown(false);
            setShowSortDropdown(false);
          }}
        >
          Filters
        </Button>

        {/* Mobile-only: Filters panel */}
        {showFilters && (
          <div className="sm:hidden absolute top-full left-0 right-0 mt-2 z-20">
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4 shadow-lg max-w-[320px]">
              <span className="text-body-bold font-body-bold text-default-font">Filters</span>

              <div className="flex w-full flex-col gap-4">
                {/* Price Range */}
                <div className="flex flex-col gap-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Price Range (XRP)
                  </span>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="flex-1 min-w-0 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="flex-1 min-w-0 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                    />
                  </div>
                </div>

                {/* Rarity Rank */}
                <div className="flex flex-col gap-2">
                  <span className="text-caption font-caption text-subtext-color">
                    Rarity Rank
                  </span>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={minRarity}
                      onChange={(e) => setMinRarity(e.target.value)}
                      className="flex-1 min-w-0 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={maxRarity}
                      onChange={(e) => setMaxRarity(e.target.value)}
                      className="flex-1 min-w-0 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                    />
                  </div>
                </div>

                {/* Points Filter */}
                {hasVariablePoints && (
                  <div className="flex flex-col gap-2">
                    <span className="text-caption font-caption text-subtext-color">
                      Richlist Points
                    </span>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={minPoints}
                        onChange={(e) => setMinPoints(e.target.value)}
                        className="flex-1 min-w-0 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={maxPoints}
                        onChange={(e) => setMaxPoints(e.target.value)}
                        className="flex-1 min-w-0 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                      />
                    </div>
                  </div>
                )}

                {/* Trait Filters */}
                {traitStats.length > 0 && (
                  <div className="flex flex-col gap-4">
                    <span className="text-caption font-caption text-subtext-color">
                      Traits
                    </span>
                    <div className="grid grid-cols-1 gap-4 max-h-[300px] overflow-y-auto">
                      {traitStats.map((trait) => {
                        const isExpanded = expandedTraits.has(trait.traitType);
                        return (
                          <div key={trait.traitType} className="flex flex-col gap-2">
                            <button
                              onClick={() => toggleTraitExpanded(trait.traitType)}
                              className="flex items-center gap-2 text-left cursor-pointer hover:opacity-70 transition-opacity"
                            >
                              <FeatherChevronRight
                                className={`text-caption font-caption text-subtext-color transition-transform ${
                                  isExpanded ? 'rotate-90' : ''
                                }`}
                              />
                              <span className="text-caption-bold font-caption-bold text-default-font">
                                {trait.traitType}
                              </span>
                            </button>
                            {isExpanded && (
                              <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                                {trait.values.map((val) => {
                                  const isSelected = selectedTraits[trait.traitType]?.includes(val.value);
                                  const rarityInfo = getRarityInfoInCategory(val.percentage, trait.values.map(v => v.percentage));
                                  const isLegendary = rarityInfo.tier === 'legendary';
                                  return (
                                    <label
                                      key={val.value}
                                      className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!isSelected}
                                        onChange={() => handleTraitToggle(trait.traitType, val.value)}
                                        className="cursor-pointer"
                                      />
                                      <span
                                        className={`text-caption font-caption flex-1 ${isLegendary ? 'legendary-shimmer font-semibold' : ''}`}
                                        style={!isLegendary ? { color: rarityInfo.color } : {}}
                                      >
                                        {val.value}
                                      </span>
                                      <span
                                        className={`text-caption font-caption ${isLegendary ? 'legendary-shimmer' : ''}`}
                                        style={!isLegendary ? { color: rarityInfo.color, opacity: 0.8 } : {}}
                                      >
                                        {val.percentage.toFixed(1)}%
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Filter Actions */}
                <div className="flex gap-2">
                  <Button variant="brand-primary" onClick={() => { handleFilterApply(); setShowFilters(false); }}>
                    Apply Filters
                  </Button>
                  <Button variant="neutral-secondary" onClick={handleFilterClear}>
                    Clear All
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sort Dropdown */}
        <div className="relative">
          <Button
            variant="neutral-secondary"
            iconRight={<FeatherChevronDown />}
            onClick={() => {
              setShowSortDropdown(!showSortDropdown);
              setShowOwnershipDropdown(false);
              setShowFilters(false);
            }}
          >
            {getCurrentSortLabel()}
          </Button>

          {showSortDropdown && (
            <div className="absolute top-full left-0 mt-2 w-56 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-10">
              {getAvailableSortOptions(hasVariablePoints).map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    handleSortChange(option.value);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                    sortOption === option.value ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <TextField
          className="h-auto w-64 flex-none"
          variant="filled"
          label=""
          helpText=""
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder="Search by name or ID"
            value={searchQuery}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchQuery(event.target.value);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                handleFilterApply();
              }
            }}
          />
        </TextField>

        {/* Spacer + right content */}
        {rightContent && (
          <>
            <div className="flex-1" />
            {rightContent}
          </>
        )}
      </div>

      {/* Desktop-only: Filters Panel */}
      {showFilters && (
        <div className="hidden sm:flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4">
          <span className="text-body-bold font-body-bold text-default-font">Filters</span>

          <div className="flex w-full flex-col gap-4">
            {/* Price Range */}
            <div className="flex flex-col gap-2">
              <span className="text-caption font-caption text-subtext-color">
                Price Range (XRP)
              </span>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-32 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-32 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                />
              </div>
            </div>

            {/* Rarity Rank */}
            <div className="flex flex-col gap-2">
              <span className="text-caption font-caption text-subtext-color">
                Rarity Rank
              </span>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={minRarity}
                  onChange={(e) => setMinRarity(e.target.value)}
                  className="w-32 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={maxRarity}
                  onChange={(e) => setMaxRarity(e.target.value)}
                  className="w-32 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                />
              </div>
            </div>

            {/* Points Filter */}
            {hasVariablePoints && (
              <div className="flex flex-col gap-2">
                <span className="text-caption font-caption text-subtext-color">
                  Richlist Points
                </span>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPoints}
                    onChange={(e) => setMinPoints(e.target.value)}
                    className="w-32 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPoints}
                    onChange={(e) => setMaxPoints(e.target.value)}
                    className="w-32 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font"
                  />
                </div>
              </div>
            )}

            {/* Trait Filters */}
            {traitStats.length > 0 && (
              <div className="flex flex-col gap-4">
                <span className="text-caption font-caption text-subtext-color">
                  Traits
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {traitStats.map((trait) => {
                    const isExpanded = expandedTraits.has(trait.traitType);
                    return (
                      <div key={trait.traitType} className="flex flex-col gap-2">
                        <button
                          onClick={() => toggleTraitExpanded(trait.traitType)}
                          className="flex items-center gap-2 text-left cursor-pointer hover:opacity-70 transition-opacity"
                        >
                          <FeatherChevronRight
                            className={`text-caption font-caption text-subtext-color transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`}
                          />
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            {trait.traitType}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                            {trait.values.map((val) => {
                              const isSelected = selectedTraits[trait.traitType]?.includes(val.value);
                              const rarityInfo = getRarityInfoInCategory(val.percentage, trait.values.map(v => v.percentage));
                              const isLegendary = rarityInfo.tier === 'legendary';
                              return (
                                <label
                                  key={val.value}
                                  className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 px-2 py-1 rounded"
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!isSelected}
                                    onChange={() => handleTraitToggle(trait.traitType, val.value)}
                                    className="cursor-pointer"
                                  />
                                  <span
                                    className={`text-caption font-caption flex-1 ${isLegendary ? 'legendary-shimmer font-semibold' : ''}`}
                                    style={!isLegendary ? { color: rarityInfo.color } : {}}
                                  >
                                    {val.value}
                                  </span>
                                  <span
                                    className={`text-caption font-caption ${isLegendary ? 'legendary-shimmer' : ''}`}
                                    style={!isLegendary ? { color: rarityInfo.color, opacity: 0.8 } : {}}
                                  >
                                    {val.percentage.toFixed(1)}%
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filter Actions */}
            <div className="flex gap-2">
              <Button variant="brand-primary" onClick={handleFilterApply}>
                Apply Filters
              </Button>
              <Button variant="neutral-secondary" onClick={handleFilterClear}>
                Clear All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex w-full items-center gap-2 flex-wrap">
          <span className="text-caption font-caption text-subtext-color">Active filters:</span>
          {/* Price Range Badge */}
          {(minPrice || maxPrice) && (
            <Badge
              variant="neutral"
              iconRight={
                <button onClick={handleClearPriceFilter}>
                  <FeatherX className="w-3 h-3" />
                </button>
              }
            >
              Price: {minPrice && maxPrice ? `${minPrice} - ${maxPrice}` : minPrice ? `≥${minPrice}` : `≤${maxPrice}`} XRP
            </Badge>
          )}
          {/* Rarity Rank Badge */}
          {(minRarity || maxRarity) && (
            <Badge
              variant="neutral"
              iconRight={
                <button onClick={handleClearRarityFilter}>
                  <FeatherX className="w-3 h-3" />
                </button>
              }
            >
              Rank: {minRarity && maxRarity ? `${minRarity} - ${maxRarity}` : minRarity ? `≥${minRarity}` : `≤${maxRarity}`}
            </Badge>
          )}
          {/* Points Badge */}
          {(minPoints || maxPoints) && (
            <Badge
              variant="neutral"
              iconRight={
                <button onClick={handleClearPointsFilter}>
                  <FeatherX className="w-3 h-3" />
                </button>
              }
            >
              Richlist Points: {minPoints && maxPoints ? `${minPoints} - ${maxPoints}` : minPoints ? `≥${minPoints}` : `≤${maxPoints}`}
            </Badge>
          )}
          {/* Trait Badges */}
          {Object.entries(selectedTraits).map(([traitType, values]) =>
            values.map((value) => (
              <Badge
                key={`${traitType}:${value}`}
                variant="neutral"
                iconRight={
                  <button onClick={() => handleTraitToggle(traitType, value)}>
                    <FeatherX className="w-3 h-3" />
                  </button>
                }
              >
                {traitType}: {value}
              </Badge>
            ))
          )}
          {activeFiltersRightContent && (
            <div className="ml-auto flex items-center">{activeFiltersRightContent}</div>
          )}
        </div>
      )}
    </div>
  );
};
